import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';

/**
 * Independent Stays plan ISR: the listing's readiness ("Ready to receive
 * guests") and the setup wizard's saved progress (BE src/stays/routes/setup.js).
 */

export type SetupStepKey = 'unit' | 'basics' | 'rules' | 'arrival' | 'calendars' | 'first_stay' | 'send_link';

export type ReadinessKey = 'basics' | 'house_rules' | 'arrival_guide' | 'door_access' | 'bookings' | 'preview' | 'open';

export interface ReadinessItem {
  key: ReadinessKey;
  done: boolean;
  label: string;
  /** The wizard step that fixes it. */
  step: SetupStepKey;
  fix_path: string;
}

export interface SetupProgress {
  step: SetupStepKey | null;
  completed: SetupStepKey[];
  previewed_at: string | null;
}

export interface ListingReadiness {
  ready: boolean;
  items: ReadinessItem[];
  setup: SetupProgress;
}

const wrap = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

const listingPath = (id: string) => `${apiRoute}/stays/listings/${encodeURIComponent(id)}`;

export const getReadiness = (listingId: string): Promise<ListingReadiness> =>
  wrap(axios.get(`${listingPath(listingId)}/readiness`), 'Could not check this listing.');

export const saveSetupProgress = (
  listingId: string,
  input: { step: SetupStepKey; completed?: SetupStepKey[]; previewed?: true }
): Promise<{ setup: SetupProgress }> =>
  wrap(axios.put(`${listingPath(listingId)}/setup`, input), 'Could not save your place in the setup.');
